// Contact Form Component
import { I18nManager } from "../../services/i18n-manager";

export class ContactForm {
  private i18nManager: I18nManager;
  private form: HTMLFormElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private filePreview: HTMLElement | null = null;
  private uploadedFiles: File[] = [];

  constructor(i18nManager: I18nManager) {
    this.i18nManager = i18nManager;
  }

  init(): void {
    this.form = document.getElementById("contact-form") as HTMLFormElement;
    this.fileInput = document.getElementById(
      "contact-files"
    ) as HTMLInputElement;
    this.filePreview = document.getElementById("file-preview");

    if (!this.form || !this.fileInput || !this.filePreview) {
      console.error("Contact form elements not found");
      return;
    }

    this.setupEventListeners();
    this.setupFileUpload();
    this.updateContent(); // Update translations on initial load
  }

  private setupEventListeners(): void {
    if (!this.form) return;

    // Form submission
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Listen for language changes
    this.i18nManager.onLanguageChange(() => {
      this.updateContent();
    });
  }

  private setupFileUpload(): void {
    if (!this.fileInput || !this.filePreview) return;

    // File input change
    this.fileInput.addEventListener("change", (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files) {
        this.handleFileSelection(Array.from(target.files));
      }
    });

    // Drag and drop
    const uploadArea = document.getElementById("file-upload");
    if (uploadArea) {
      uploadArea.addEventListener("dragover", (e) => {
        e.preventDefault();
        uploadArea.classList.add("drag-over");
      });

      uploadArea.addEventListener("dragleave", (e) => {
        e.preventDefault();
        uploadArea.classList.remove("drag-over");
      });

      uploadArea.addEventListener("drop", (e) => {
        e.preventDefault();
        uploadArea.classList.remove("drag-over");

        const files = Array.from(e.dataTransfer?.files || []);
        this.handleFileSelection(files);
      });

      // Click to browse
      uploadArea.addEventListener("click", () => {
        this.fileInput?.click();
      });
    }
  }

  private handleFileSelection(files: File[]): void {
    const validFiles = files.filter((file) => this.validateFile(file));

    if (validFiles.length === 0) {
      this.showError(this.i18nManager.t("error.invalid_file_type"));
      return;
    }

    this.uploadedFiles = [...this.uploadedFiles, ...validFiles];
    this.renderFilePreview();
  }

  private validateFile(file: File): boolean {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      "application/sla", // STL files
      "application/x-navistyle", // STL files
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (file.size > maxSize) {
      this.showError(this.i18nManager.t("error.file_too_large"));
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      this.showError(this.i18nManager.t("error.invalid_file_type"));
      return false;
    }

    return true;
  }

  private renderFilePreview(): void {
    if (!this.filePreview) return;

    if (this.uploadedFiles.length === 0) {
      this.filePreview.innerHTML = "";
      return;
    }

    const fileItems = this.uploadedFiles
      .map(
        (file, index) => `
      <div class="file-item" data-index="${index}">
        <div class="file-info">
          <div class="file-icon">
            ${this.getFileIcon(file)}
          </div>
          <div class="file-details">
            <div class="file-name">${file.name}</div>
            <div class="file-size">${this.formatFileSize(file.size)}</div>
          </div>
        </div>
        <button class="file-remove" data-index="${index}" aria-label="Remove file">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `
      )
      .join("");

    this.filePreview.innerHTML = `
      <div class="file-preview-header">
        <h4>${this.i18nManager.t("contact.selected_files")}</h4>
        <button class="clear-files glass-button" type="button">
          ${this.i18nManager.t("common.clear")}
        </button>
      </div>
      <div class="file-list">
        ${fileItems}
      </div>
    `;

    // Setup remove file events
    this.filePreview.querySelectorAll(".file-remove").forEach((button) => {
      button.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        const index = parseInt(target.getAttribute("data-index") || "0");
        this.removeFile(index);
      });
    });

    // Setup clear all files event
    const clearButton = this.filePreview.querySelector(".clear-files");
    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.clearFiles();
      });
    }
  }

  private getFileIcon(file: File): string {
    if (file.type.startsWith("image/")) {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21,15 16,10 5,21"/>
        </svg>
      `;
    } else {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
        </svg>
      `;
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  private removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.renderFilePreview();
  }

  private clearFiles(): void {
    this.uploadedFiles = [];
    this.renderFilePreview();
    if (this.fileInput) {
      this.fileInput.value = "";
    }
  }

  private async handleSubmit(): Promise<void> {
    if (!this.form) return;

    try {
      this.showLoading();
      // 1) Upload files and collect URLs
      const uploadedUrls: string[] = [];
      for (const file of this.uploadedFiles) {
        const fd = new FormData();
        fd.append("file", file);
        const uploadRes = await fetch("/api/contact/upload", {
          method: "POST",
          body: fd,
        });
        if (!uploadRes.ok) {
          throw new Error(`Upload failed: ${uploadRes.status}`);
        }
        const uploadJson = await uploadRes.json();
        if (uploadJson.file_url) {
          uploadedUrls.push(uploadJson.file_url);
        }
      }

      // 2) Submit contact request JSON
      const name =
        (this.form.querySelector("#contact-name") as HTMLInputElement)?.value ||
        "";
      const email =
        (this.form.querySelector("#contact-email") as HTMLInputElement)
          ?.value || "";
      const message =
        (this.form.querySelector("#contact-message") as HTMLTextAreaElement)
          ?.value || "";

      const submitRes = await fetch("/api/contact/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          file_urls: uploadedUrls,
        }),
      });
      if (!submitRes.ok) {
        throw new Error(`Submit failed: ${submitRes.status}`);
      }

      this.showSuccess(this.i18nManager.t("success.contact_submitted"));
      this.resetForm();
    } catch (error) {
      console.error("Failed to submit contact form:", error);
      this.showError(this.i18nManager.t("error.submit_failed"));
    } finally {
      this.hideLoading();
    }
  }

  private showLoading(): void {
    const submitButton = this.form?.querySelector(
      ".contact-submit"
    ) as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = this.i18nManager.t("common.loading");
    }
  }

  private hideLoading(): void {
    const submitButton = this.form?.querySelector(
      ".contact-submit"
    ) as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = this.i18nManager.t("contact.send_request");
    }
  }

  private showSuccess(message: string): void {
    // Show notification (top-right corner)
    this.showNotification(message, "success");
    
    // Also show inline success message in the form
    this.showInlineSuccess(message);
  }
  
  private showInlineSuccess(message: string): void {
    if (!this.form) return;
    
    // Remove any existing success/error messages
    const existingMessage = this.form.querySelector(".form-message");
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create success message element
    const successMessage = document.createElement("div");
    successMessage.className = "form-message form-message--success";
    successMessage.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span>${message}</span>
    `;
    
    // Insert before submit button
    const submitButton = this.form.querySelector(".contact-submit");
    if (submitButton && submitButton.parentElement) {
      submitButton.parentElement.insertBefore(successMessage, submitButton);
    } else {
      // Fallback: append to form
      this.form.appendChild(successMessage);
    }
    
    // Scroll to success message
    successMessage.scrollIntoView({ behavior: "smooth", block: "center" });
    
    // Remove after 10 seconds
    setTimeout(() => {
      if (successMessage.parentElement) {
        successMessage.classList.add("fade-out");
        setTimeout(() => {
          successMessage.remove();
        }, 300);
      }
    }, 10000);
  }

  private showError(message: string): void {
    // Show notification (top-right corner)
    this.showNotification(message, "error");
    
    // Also show inline error message in the form
    this.showInlineError(message);
  }
  
  private showInlineError(message: string): void {
    if (!this.form) return;
    
    // Remove any existing success/error messages
    const existingMessage = this.form.querySelector(".form-message");
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create error message element
    const errorMessage = document.createElement("div");
    errorMessage.className = "form-message form-message--error";
    errorMessage.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>${message}</span>
    `;
    
    // Insert before submit button
    const submitButton = this.form.querySelector(".contact-submit");
    if (submitButton && submitButton.parentElement) {
      submitButton.parentElement.insertBefore(errorMessage, submitButton);
    } else {
      // Fallback: append to form
      this.form.appendChild(errorMessage);
    }
    
    // Scroll to error message
    errorMessage.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  private showNotification(message: string, type: "success" | "error"): void {
    const notification = document.createElement("div");
    notification.className = `notification notification--${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        ${type === "success" 
          ? `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
               <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
               <polyline points="22 4 12 14.01 9 11.01"></polyline>
             </svg>`
          : `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
               <circle cx="12" cy="12" r="10"></circle>
               <line x1="12" y1="8" x2="12" y2="12"></line>
               <line x1="12" y1="16" x2="12.01" y2="16"></line>
             </svg>`
        }
        <span>${message}</span>
      </div>
    `;

    document.body.appendChild(notification);

    requestAnimationFrame(() => {
      notification.classList.add("show");
    });

    // Keep success notifications visible longer (8 seconds instead of 5)
    const duration = type === "success" ? 8000 : 5000;
    setTimeout(() => {
      notification.classList.remove("show");
      setTimeout(() => {
        if (notification.parentElement) {
        document.body.removeChild(notification);
        }
      }, 300);
    }, duration);
  }

  private resetForm(): void {
    if (this.form) {
      this.form.reset();
    }
    this.clearFiles();
  }

  private updateContent(): void {
    // Update form labels and placeholders based on current language
    const nameLabel = document.querySelector('label[for="contact-name"]');
    const emailLabel = document.querySelector('label[for="contact-email"]');
    const messageLabel = document.querySelector('label[for="contact-message"]');
    const filesLabel = document.querySelector('label[for="contact-files"]');
    const submitButton = document.querySelector(".contact-submit");

    if (nameLabel) nameLabel.textContent = this.i18nManager.t("contact.name");
    if (emailLabel)
      emailLabel.textContent = this.i18nManager.t("contact.email");
    if (messageLabel)
      messageLabel.textContent = this.i18nManager.t("contact.message");
    if (filesLabel)
      filesLabel.textContent = this.i18nManager.t("contact.files");
    if (submitButton)
      submitButton.textContent = this.i18nManager.t("contact.send_request");

    // Update file upload area text
    const uploadArea = document.getElementById("file-upload");
    if (uploadArea) {
      const uploadText = uploadArea.querySelector("p");
      const fileInfo = uploadArea.querySelector(".file-info");
      
      if (uploadText) {
        uploadText.textContent = this.i18nManager.t("contact.file_upload_hint") ||
          "Drag and drop files here or click to browse";
      }
      
      if (fileInfo) {
        fileInfo.textContent = this.i18nManager.t("contact.file_types") ||
          "STL files, images (JPG, PNG, WebP)";
      }
    }
  }

  // Public methods
  public getUploadedFiles(): File[] {
    return this.uploadedFiles;
  }

  public clearUploadedFiles(): void {
    this.clearFiles();
  }

  public addFile(file: File): boolean {
    if (this.validateFile(file)) {
      this.uploadedFiles.push(file);
      this.renderFilePreview();
      return true;
    }
    return false;
  }
}
